import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, Signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import { PaceNotificationLongtext } from './pace-notification-longtext.interface';

/**
 * The notification and pre-clarification long texts of a notification, side by side on a large
 * screen and stacked on a small one.
 *
 * Both blocks fold: the pair of texts through the expander at the bottom, the pre-clarification
 * on its own — but only where the host declared it collapsible, which is what keeps it open on a
 * desktop and folded on a smartphone.
 */
@Component({
    selector: 'pace-notification-longtext',
    imports: [FontAwesomeModule, NgTemplateOutlet],
    templateUrl: './pace-notification-longtext.component.html',
    styleUrls: ['./pace-notification-longtext.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaceNotificationLongtextComponent {
    public readonly longtext = input.required<PaceNotificationLongtext>();

    public readonly faChevronDown = faChevronDown;
    public readonly faChevronUp = faChevronUp;
    public readonly faChevronRight = faChevronRight;

    public readonly isCollapsed = signal(true);
    private readonly isPreclarificationCollapsed = signal(true);

    /** A pre-clarification the host does not allow folding is always shown. */
    public readonly isPreclarificationHidden: Signal<boolean> = computed(
        () => this.longtext().isPreclarificationCollapsible && this.isPreclarificationCollapsed(),
    );

    public constructor() {
        // Whether the pre-clarification starts folded follows the screen size the host reports, so
        // rotating from smartphone to tablet width opens it again.
        effect(() => this.isPreclarificationCollapsed.set(this.longtext().isPreclarificationCollapsible));
    }

    public toggleCollapsed(): void {
        this.isCollapsed.update((collapsed) => !collapsed);
    }

    public togglePreclarification(): void {
        if (this.longtext().isPreclarificationCollapsible) {
            this.isPreclarificationCollapsed.update((collapsed) => !collapsed);
        }
    }
}
